import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import UserRole from '@careerstart/wae-common/src/main/constants/user-role';
import LanguageConverter from '@careerstart/wae-common/src/main/helperFunction/LanguageConverter';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import CelebrationIcon from '@mui/icons-material/Celebration';
import CheckIcon from '@mui/icons-material/Check';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import GroupIcon from '@mui/icons-material/Group';
import { Box, Checkbox, Typography } from '@mui/material';

import { ReactComponent as AvgPaceIcon } from '../../assets/icons/AvgPaceIcon.svg';
import { ReactComponent as FlipOverIcon } from '../../assets/icons/FlipOverIcon.svg';
import { ReactComponent as PaceIcon } from '../../assets/icons/PaceIcon.svg';
import selectUser from '../../store/selectors/appSelector';
import { BLACK, FEED_BACK, PRIMARY_COLOR, RED } from '../../theme/colorConstants';
import { PRIMARY_FONT } from '../../theme/fontConstants';

import {
  CARD_VARIANTS,
  formatJobDate,
  getCardVariant,
  getFlipActionKey,
  getOverstaffStats,
  getPlacementsNeeded,
} from './jobOrderCardModel';

const TEXT_BLACK = '#000000';

const EMPLOYER_STAFFING_BANNER = {
  background: BLACK[20],
  titleColor: TEXT_BLACK,
  checkboxColor: BLACK[70],
};

const BANNER_STYLES = {
  [CARD_VARIANTS.NEEDS_ATTENTION]: {
    background: RED[0],
    titleColor: RED[80],
    checkboxColor: BLACK[70],
  },
  [CARD_VARIANTS.ALL_OPENINGS_FILLED]: {
    background: '#E9FBED',
    titleColor: TEXT_BLACK,
    checkboxColor: BLACK[70],
  },
  [CARD_VARIANTS.FULLY_STAFFED]: {
    background: `linear-gradient(to bottom, ${PRIMARY_COLOR[60]}, ${PRIMARY_COLOR[80]})`,
    titleColor: BLACK[0],
    checkboxColor: BLACK[0],
    Icon: CelebrationIcon,
    iconGap: '6px',
  },
  [CARD_VARIANTS.JOB_STARTED]: {
    background: `linear-gradient(to bottom, ${FEED_BACK.ORANGE_20}, #C98603)`,
    titleColor: BLACK[0],
    checkboxColor: BLACK[0],
    Icon: AvgPaceIcon,
    svgIcon: true,
  },
  [CARD_VARIANTS.JOB_COMPLETE]: {
    background: `linear-gradient(to bottom, ${FEED_BACK.GREEN_70}, ${FEED_BACK.GREEN_80})`,
    titleColor: BLACK[0],
    checkboxColor: BLACK[0],
    Icon: CheckCircleIcon,
    iconTestId: 'job-order-card-complete-icon',
  },
  [CARD_VARIANTS.CANCELLED]: {
    background: BLACK[30],
    titleColor: BLACK[80],
    checkboxColor: BLACK[80],
  },
};

const selectionBoxSx = {
  width: 20,
  height: 20,
  boxSizing: 'border-box',
  border: '1.67px solid currentColor',
  borderRadius: '3px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const FLIP_ICONS = {
  'jobOrder.finalizeTimeCard': { Icon: BorderColorIcon },
  'jobOrder.startCheckInAction': { Icon: PaceIcon, svgIcon: true },
  'jobOrder.viewRoster': { Icon: GroupIcon },
  'jobOrder.viewJob': { Icon: FlipOverIcon, svgIcon: true },
};

const JobOrderCardChrome = ({
  jobOrder,
  timeStatus,
  isCancelled,
  isFlipped,
  onFlipClick,
  showSelection,
  checked,
  disabled,
  onToggle,
}) => {
  const user = useSelector(selectUser);
  const isEmployer = user?.role === UserRole.EMPLOYER;
  const variant = getCardVariant({ jobOrder, timeStatus, isCancelled });
  const isEmployerStaffingBanner =
    isEmployer &&
    (variant === CARD_VARIANTS.NEEDS_ATTENTION || variant === CARD_VARIANTS.ALL_OPENINGS_FILLED);
  const palette = isEmployerStaffingBanner ? EMPLOYER_STAFFING_BANNER : BANNER_STYLES[variant];
  const placementsNeeded = LanguageConverter(
    'jobOrder.placementsNeeded',
    getPlacementsNeeded(jobOrder)
  );
  const overstaffAvailable = LanguageConverter(
    'jobOrder.overstaffAvailable',
    getOverstaffStats(jobOrder).available
  );
  const titles = {
    [CARD_VARIANTS.NEEDS_ATTENTION]: LanguageConverter(
      isEmployer ? 'jobOrder.filling' : 'jobOrder.needsAttention'
    ),
    [CARD_VARIANTS.ALL_OPENINGS_FILLED]: LanguageConverter('jobOrder.allOpeningsFilled'),
    [CARD_VARIANTS.FULLY_STAFFED]: LanguageConverter('jobOrder.fullyStaffed'),
    [CARD_VARIANTS.JOB_STARTED]: LanguageConverter('jobOrder.jobStarted'),
    [CARD_VARIANTS.JOB_COMPLETE]: LanguageConverter('jobOrder.jobComplete'),
    [CARD_VARIANTS.CANCELLED]: LanguageConverter('jobOrder.shiftCancelled'),
  };
  const subtitles = {
    [CARD_VARIANTS.NEEDS_ATTENTION]: placementsNeeded,
    [CARD_VARIANTS.ALL_OPENINGS_FILLED]: overstaffAvailable,
  };
  const title = titles[variant];
  const subtitle = subtitles[variant];
  const flipActionKey = getFlipActionKey({ isFlipped, isCancelled, timeStatus });
  const flipLabels = {
    'jobOrder.viewJob': LanguageConverter('jobOrder.viewJob'),
    'jobOrder.finalizeTimeCard': LanguageConverter('jobOrder.finalizeTimeCard'),
    'jobOrder.startCheckInAction': LanguageConverter('jobOrder.startCheckInAction'),
    'jobOrder.viewRoster': LanguageConverter('jobOrder.viewRoster'),
  };
  const selectJobOrderLabel = LanguageConverter('jobOrders.selectJobOrder');
  const flipLabel = flipLabels[flipActionKey] || '';
  const BannerIcon = palette.Icon;
  const flipIcon = FLIP_ICONS[flipActionKey];
  const FlipIcon = flipIcon?.Icon;
  const bannerIcon = (() => {
    if (!BannerIcon) return null;
    if (palette.svgIcon) {
      return <BannerIcon width={20} height={20} aria-hidden />;
    }
    return <BannerIcon data-testid={palette.iconTestId} sx={{ fontSize: 20 }} />;
  })();
  const flipIconNode = (() => {
    if (!FlipIcon) return null;
    if (flipIcon.svgIcon) {
      return <FlipIcon width={14} height={14} aria-hidden />;
    }
    return <FlipIcon sx={{ fontSize: 14 }} />;
  })();

  return (
    <Box>
      <Box
        data-testid="job-order-card-banner"
        data-variant={variant}
        sx={{
          height: '52px',
          minHeight: '52px',
          maxHeight: '52px',
          boxSizing: 'border-box',
          flexShrink: 0,
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
          px: '24px',
          background: palette.background,
          color: palette.titleColor,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: palette.iconGap || '4px',
            textAlign: 'left',
            minWidth: 0,
          }}
        >
          {bannerIcon}
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontFamily: PRIMARY_FONT[700],
                fontSize: '14px',
                lineHeight: '20px',
                color: 'inherit',
              }}
            >
              {title}
            </Typography>
            {subtitle ? (
              <Typography
                sx={{
                  fontFamily: PRIMARY_FONT[400],
                  fontSize: '12px',
                  lineHeight: '16px',
                  color: TEXT_BLACK,
                }}
              >
                {subtitle}
              </Typography>
            ) : null}
          </Box>
        </Box>
        {showSelection ? (
          <Checkbox
            checked={!!checked}
            disabled={disabled}
            disableRipple
            onClick={(event) => event.stopPropagation()}
            onChange={onToggle}
            icon={<Box component="span" sx={selectionBoxSx} />}
            checkedIcon={
              <Box component="span" sx={selectionBoxSx}>
                <CheckIcon sx={{ fontSize: 16 }} />
              </Box>
            }
            inputProps={{ 'aria-label': selectJobOrderLabel }}
            data-testid="job-order-select-checkbox"
            sx={{
              color: palette.checkboxColor,
              p: 0,
              width: 20,
              height: 20,
              flexShrink: 0,
              '&.Mui-checked': { color: palette.checkboxColor },
            }}
          />
        ) : null}
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
          px: '24px',
          pt: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: '14px',
            color: BLACK[80],
            fontFamily: PRIMARY_FONT[500],
            lineHeight: '20px',
          }}
        >
          {formatJobDate(jobOrder?.start, jobOrder?.timeZone)}
        </Typography>
        {flipActionKey ? (
          <Box
            component="button"
            type="button"
            data-testid="job-order-card-flip"
            onClick={onFlipClick}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              border: 0,
              borderBottom: `0.5px dashed ${BLACK[80]}`,
              background: 'transparent',
              cursor: 'pointer',
              color: BLACK[80],
              fontFamily: PRIMARY_FONT[500],
              fontSize: '14px',
              letterSpacing: '-0.28px',
              lineHeight: '20px',
              p: 0,
            }}
          >
            {flipIconNode}
            {flipLabel}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

JobOrderCardChrome.propTypes = {
  jobOrder: PropTypes.shape({
    start: PropTypes.number,
    timeZone: PropTypes.string,
    numOpenings: PropTypes.number,
    numOverstaff: PropTypes.number,
    placementsCount: PropTypes.number,
  }),
  timeStatus: PropTypes.string,
  isCancelled: PropTypes.bool,
  isFlipped: PropTypes.bool,
  onFlipClick: PropTypes.func,
  showSelection: PropTypes.bool,
  checked: PropTypes.bool,
  disabled: PropTypes.bool,
  onToggle: PropTypes.func,
};

export default JobOrderCardChrome;
